// Lógica de contadores da tela de Sessão (Etapa 12 — spec S03). Toda
// escrita passa pelos helpers transacionais de src/db/writes.ts — regra
// invíolável #2 do projeto. `counter_events` é append-only (ver
// insertCounterEvent em src/db/writes.ts): cada toque grava um evento novo,
// nunca atualiza/apaga um existente; o valor exibido é sempre a SOMA dos
// deltas, recalculada aqui — nunca armazenada.
import { and, eq, isNull } from 'drizzle-orm';
import type { InferInsertModel } from 'drizzle-orm';
import { counters, counterEvents, type Counter } from '../../db/schema';
import { insertRecord, updateRecord, softDeleteRecord, insertCounterEvent } from '../../db/writes';
import type { AppDatabase } from '../../db/outbox';

/** Nome padrão do contador primário criado com todo projeto (spec S03). */
export const PRIMARY_COUNTER_NAME = 'Carreiras';

/** Máximo de contadores EXTRAS por projeto (o primário não conta para este
 * limite) — "não virar planilha" (spec S03). Constante configurável. */
export const MAX_EXTRA_COUNTERS = 8;

export class ExtraCounterLimitError extends Error {
  readonly projectId: string;

  constructor(projectId: string) {
    super(`Limite de contadores extras atingido para o projeto: ${projectId}`);
    this.name = 'ExtraCounterLimitError';
    this.projectId = projectId;
  }
}

export class CannotDeletePrimaryCounterError extends Error {
  readonly counterId: string;

  constructor(counterId: string) {
    super(`Não é possível excluir o contador primário: ${counterId}`);
    this.name = 'CannotDeletePrimaryCounterError';
    this.counterId = counterId;
  }
}

/** Todos os contadores não excluídos (soft delete) de um projeto — primário + extras. */
export async function listCounters(db: AppDatabase, projectId: string): Promise<Counter[]> {
  return db
    .select()
    .from(counters)
    .where(and(eq(counters.projectId, projectId), isNull(counters.deletedAt)));
}

/**
 * Garante que o projeto tem um contador primário ("Carreiras", `is_primary
 * = true`) e devolve ele. `createProject` (Etapa 10,
 * src/features/projects/projectRepository.ts) NÃO cria esse contador — foi
 * verificado ao ler o arquivo antes de escrever este módulo — então ele é
 * criado aqui, na primeira vez que a tela de Sessão abre para o projeto
 * (lazy). Idempotente: chamadas repetidas não criam um segundo primário.
 */
export async function ensurePrimaryCounter(db: AppDatabase, projectId: string): Promise<Counter> {
  const existing = await db
    .select()
    .from(counters)
    .where(and(eq(counters.projectId, projectId), eq(counters.isPrimary, true), isNull(counters.deletedAt)))
    .get();
  if (existing) {
    return existing;
  }

  // NB: passar o objeto por uma `const` em vez de literal direto no argumento
  // não é estilo — contorna um bug conhecido de checagem de "excess property"
  // do TypeScript para tipos gerados por mapped/conditional types sobre um
  // parâmetro genérico de união (`InferInsertModel<TTable>` do Drizzle sobre
  // `WritableTable`, ver src/db/writes.ts). Colunas OPCIONAIS (com default,
  // como `name`/`isPrimary` em `counters`) disparam um falso "property does
  // not exist" quando passadas como literal fresco direto no argumento; a
  // checagem de excess-property só se aplica a literais "frescos" — uma
  // variável já tipada passa pela checagem normal de atribuição (que aceita
  // propriedades extras estruturalmente). Comprovado empiricamente: chamadas
  // equivalentes só com colunas obrigatórias (ex. `insertRecord(db, projects,
  // { userId, name })` em projectRepository.ts) não sofrem disso, porque
  // `name` ali é `notNull()` sem default (obrigatória, não passa pelo mesmo
  // ramo condicional do tipo). Mesma técnica usada abaixo e em
  // timeEntries.ts.
  const data = { projectId, name: PRIMARY_COUNTER_NAME, isPrimary: true };
  return insertRecord(db, counters, data);
}

/**
 * Cria um contador extra. Bloqueia no cliente ao atingir `MAX_EXTRA_COUNTERS`
 * — não deixa a escrita nem chegar ao banco quando o limite já foi atingido.
 */
export async function createExtraCounter(db: AppDatabase, projectId: string, name: string): Promise<Counter> {
  const existing = await listCounters(db, projectId);
  const extrasCount = existing.filter((counter) => !counter.isPrimary).length;
  if (extrasCount >= MAX_EXTRA_COUNTERS) {
    throw new ExtraCounterLimitError(projectId);
  }

  const data = { projectId, name, isPrimary: false };
  return insertRecord(db, counters, data);
}

/** Forma explícita (não derivada genericamente) do patch de renomear — ver
 * comentário grande abaixo em `renameCounter` sobre por que o tipo é
 * declarado assim em vez de deixar o TS inferir/derivar de
 * `InferInsertModel<typeof counters>` diretamente. */
interface CounterNamePatch {
  name: string;
}

export async function renameCounter(db: AppDatabase, counterId: string, name: string): Promise<Counter> {
  // `patch` é checado normalmente contra `CounterNamePatch` (interface
  // escrita à mão, com as chaves reais) — um erro de digitação aqui (ex.
  // `{ nome: name }`) ainda quebra a build nesta linha. O cast abaixo, no
  // ÚNICO ponto de chamada a `updateRecord`, existe só para contornar uma
  // limitação conhecida do TypeScript ao combinar o tipo condicional/mapped
  // `InferInsertModel` do Drizzle com o parâmetro genérico de união
  // `WritableTable` (src/db/writes.ts): colunas OPCIONAIS (com default ou
  // nullable, como `counters.name`) fazem o TS relatar falsamente que a
  // propriedade "não existe" (excess-property) e depois, ao tentar
  // contornar com uma variável tipada, que o objeto "não tem nenhuma
  // propriedade em comum" com o alvo (weak-type check) — comprovado
  // empiricamente que isso acontece mesmo com um alias nomeado igual ao
  // usado com sucesso em `updateProject`/`ProjectPatch`
  // (src/features/projects/projectRepository.ts), e que o comportamento
  // chega a divergir entre duas variáveis de tipo idêntico dependendo só da
  // ordem de declaração no arquivo — sinal de que não é uma peculiaridade
  // de forma de tipo confiável de contornar "certo", só um bug do
  // verificador. O mesmo padrão de cast pontual já é usado internamente em
  // `asTable()` (src/db/writes.ts) para a mesma classe de limitação; aqui é
  // só aplicado do lado de fora, no argumento, em vez de dentro do helper.
  const patch: CounterNamePatch = { name };
  return updateRecord(db, counters, counterId, patch as Partial<InferInsertModel<typeof counters>>);
}

/**
 * Soft delete de um contador EXTRA. O contador primário nunca pode ser
 * excluído pela UI (toda sessão precisa de "Carreiras") — bloqueado aqui em
 * tempo de execução, não só escondendo o botão na UI. Os `counter_events`
 * já gravados para o contador excluído NÃO são apagados (append-only,
 * histórico preservado — regra da spec S03/S05).
 */
export async function deleteExtraCounter(db: AppDatabase, counter: Pick<Counter, 'id' | 'isPrimary'>): Promise<void> {
  if (counter.isPrimary) {
    throw new CannotDeletePrimaryCounterError(counter.id);
  }
  await softDeleteRecord(db, counters, counter.id);
}

/** Valor atual do contador = soma de todos os deltas já gravados (append-only, nunca armazenado). */
export async function getCounterValue(db: AppDatabase, counterId: string): Promise<number> {
  const events = await db.select().from(counterEvents).where(eq(counterEvents.counterId, counterId));
  return events.reduce((sum, event) => sum + event.delta, 0);
}

/**
 * `−` com a contagem já em zero não pode ir para negativo (spec S03: "não
 * existe carreira negativa") — função pura, testável sem banco, usada tanto
 * aqui quanto pela UI para decidir se o botão `−` deve ficar desabilitado.
 */
export function canDecrement(currentValue: number): boolean {
  return currentValue > 0;
}

/**
 * Registra um toque em `+`/`−`. `delta = -1` com `currentValue <= 0` é
 * bloqueado ANTES de chamar `insertCounterEvent` — não grava evento nenhum
 * (não existe "desfazer" um mínimo que nunca existiu). Devolve o novo valor
 * (materializado no cliente, não a soma recalculada de novo do banco — mais
 * barato, e correto porque cada toque grava exatamente um evento).
 */
export async function tapCounter(
  db: AppDatabase,
  counterId: string,
  delta: 1 | -1,
  currentValue: number
): Promise<number> {
  if (delta === -1 && !canDecrement(currentValue)) {
    return currentValue;
  }

  await insertCounterEvent(db, { counterId, delta });
  return currentValue + delta;
}
